import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  AccessTime as AccessTimeIcon,
  LocalOffer as LocalOfferIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Typography,
  Paper,
  Chip,
  Button,
  alpha,
  LinearProgress,
  Stack,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { tokens } from "../../theme";
import { adminDataGridBoxSx, adminPageSx } from "../../theme/adminLayout";
import { API_BASE_URL } from "../../config/apiBase";
import {
  computeBookingStats,
  fetchAdminBookingsList,
  filterBookingsByTab,
} from "../../api/adminBookings";

const TAB_KEYS = ["all", "pending", "active", "completed", "canceled"];

const lifecycleChipColors = (key) => {
  switch (key) {
    case "pending":
      return { bg: alpha("#FF9800", 0.2), color: "#E65100" };
    case "active":
      return { bg: alpha("#2196F3", 0.2), color: "#1565C0" };
    case "completed":
      return { bg: alpha("#4CAF50", 0.2), color: "#2E7D32" };
    case "canceled":
      return { bg: alpha("#F44336", 0.2), color: "#C62828" };
    default:
      return { bg: alpha("#757575", 0.2), color: "#616161" };
  }
};

const paymentColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "refunded") return { bg: alpha("#FF9800", 0.2), color: "#E65100" };
  if (["paid", "succeeded", "complete", "completed"].includes(s))
    return { bg: alpha("#4CAF50", 0.2), color: "#2E7D32" };
  return { bg: alpha("#757575", 0.15), color: "#616161" };
};

const BookingsTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const baseColors = tokens(theme.palette.mode);
  const colors = {
    ...baseColors,
    blueAccent: {
      500: "#22a3f1",
      400: "#67baf0",
    },
  };

  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  const tabKey = TAB_KEYS[tab];
  const stats = useMemo(() => computeBookingStats(allRows), [allRows]);
  const rows = useMemo(
    () => filterBookingsByTab(allRows, tabKey),
    [allRows, tabKey]
  );

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const list = await fetchAdminBookingsList();
      const mapped = list.map((b) => ({
        id: b.Id,
        lifecycleKey: b.lifecycleKey,
        lifecycleLabel: b.lifecycleLabel,
        startDate: b.StartDate,
        endDate: b.EndDate,
        totalPrice: b.TotalPrice,
        type: b.type,
        status: b.status,
        vehicleId: b.VehicleId,
        renterId: b.RenterId,
        sellerId: b.sellerId,
        renterName: b.renterName || "—",
        renterEmail: b.renterEmail || "",
        vehicleLabel: b.vehicleLabel || "—",
        sellerName: b.sellerName || "—",
        paymentStatus: b.paymentStatus || "none",
        payoutStatus: b.payoutStatus || "none",
        requestDate: b.requestDate,
        acceptDate: b.acceptDate,
      }));
      setAllRows(mapped);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      Swal.fire("Error!", error.message || "Failed to fetch bookings.", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDeleteClick = async (id) => {
    Swal.fire({
      title: "Delete booking record?",
      html:
        "Only use for bad/test data. Canceled trips should stay for <b>history</b> and refunds.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.redAccent?.[500] || "#f44336",
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const response = await fetch(`${API_BASE_URL}/bookings/delete/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to delete booking");
        setAllRows((prev) => prev.filter((item) => item.id !== id));
        Swal.fire("Deleted", "Booking removed.", "success");
      } catch (error) {
        Swal.fire("Error!", error.message, "error");
      }
    });
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "lifecycleLabel",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const c = lifecycleChipColors(params.row.lifecycleKey);
        return (
          <Chip
            label={params.row.lifecycleLabel}
            size="small"
            sx={{ fontWeight: 700, bgcolor: c.bg, color: c.color }}
          />
        );
      },
    },
    {
      field: "renterName",
      headerName: "Renter",
      flex: 1.1,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.renterName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.renterEmail}
          </Typography>
        </Box>
      ),
    },
    {
      field: "vehicleLabel",
      headerName: "Vehicle / owner",
      flex: 1.2,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.vehicleLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.sellerName}
          </Typography>
        </Box>
      ),
    },
    {
      field: "duration",
      headerName: "Dates",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="caption">
          {formatDate(params.row.startDate)} → {formatDate(params.row.endDate)}
        </Typography>
      ),
    },
    {
      field: "totalPrice",
      headerName: "Price",
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          icon={<LocalOfferIcon />}
          label={`$${params.row.totalPrice}`}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha(colors.blueAccent[500], 0.15),
            color: colors.blueAccent[500],
          }}
        />
      ),
    },
    {
      field: "paymentStatus",
      headerName: "Payment",
      flex: 0.7,
      renderCell: (params) => {
        const c = paymentColor(params.row.paymentStatus);
        return (
          <Chip
            label={params.row.paymentStatus}
            size="small"
            sx={{ fontWeight: 600, bgcolor: c.bg, color: c.color }}
          />
        );
      },
    },
    {
      field: "payoutStatus",
      headerName: "Owner payout",
      flex: 0.75,
      renderCell: (params) => (
        <Chip label={params.row.payoutStatus} size="small" variant="outlined" />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.55,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View detail, payment, history">
            <IconButton
              onClick={() => navigate(`/bookings/${params.row.id}`)}
              sx={{
                bgcolor: alpha(colors.blueAccent[500], 0.1),
                mr: 0.5,
              }}
            >
              <VisibilityIcon sx={{ color: colors.blueAccent[500], fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete (test data only)">
            <IconButton
              onClick={() => handleDeleteClick(params.row.id)}
              sx={{ bgcolor: alpha("#f44336", 0.1) }}
            >
              <DeleteIcon sx={{ color: "#f44336", fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={adminPageSx}>
      <Box
        mb={2}
        p={{ xs: 2, md: 2.5 }}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          color="white"
          sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}
        >
          Bookings
        </Typography>
        <Typography variant="body2" color="white" sx={{ mt: 0.5, opacity: 0.95 }}>
          Active trips, cancellations, payment & payout status, and full history
        </Typography>
        <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
          <Chip
            icon={<AccessTimeIcon />}
            label={`All: ${stats.total}`}
            size="small"
            sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
          />
          <Chip label={`Pending: ${stats.pending}`} size="small" sx={{ bgcolor: "rgba(255,152,0,0.35)", color: "white" }} />
          <Chip label={`Active: ${stats.active}`} size="small" sx={{ bgcolor: "rgba(33,150,243,0.35)", color: "white" }} />
          <Chip label={`Completed: ${stats.completed}`} size="small" sx={{ bgcolor: "rgba(76,175,80,0.35)", color: "white" }} />
          <Chip label={`Canceled: ${stats.canceled}`} size="small" sx={{ bgcolor: "rgba(244,67,54,0.35)", color: "white" }} />
          <Chip label={`Refunded: ${stats.refunded}`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white" }} />
        </Stack>
      </Box>

      <Paper sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={`All (${stats.total})`} />
          <Tab label={`Pending (${stats.pending})`} />
          <Tab label={`Active trips (${stats.active})`} />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`Completed (${stats.completed})`} />
          <Tab label={`Canceled (${stats.canceled})`} />
        </Tabs>
      </Paper>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ ...adminDataGridBoxSx, overflowX: "auto" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            columnVisibilityModel={{
              id: !isMobile,
              duration: !isMobile,
              paymentStatus: !isMobile,
              payoutStatus: !isMobile,
            }}
            loading={loading}
            components={{
              Toolbar: () => (
                <Box sx={{ p: 1, display: "flex", gap: 1, alignItems: "center" }}>
                  <Button
                    size="small"
                    startIcon={<FilterListIcon />}
                    onClick={fetchBookings}
                  >
                    Refresh
                  </Button>
                  <GridToolbar />
                </Box>
              ),
              LoadingOverlay: LinearProgress,
            }}
            disableSelectionOnClick
            rowHeight={72}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default BookingsTable;
